import { createApi } from '@reduxjs/toolkit/query/react';
import { config } from '../../config/config';
import { IUserInfo } from '../../types/Auth/authInterface';
import { PasswordResetInfo } from '../../types/passwordResetInfo';
import customFetchBase from '../expiredToken/expiredToken';
import {
  IGetAllUsersResponse,
  IUserPendingActivation
} from '../../types/users';

const customFetch = customFetchBase(config.baseUrlDealers);

export const apiUsers = createApi({
  baseQuery: customFetch,
  endpoints: (builder) => ({
    getUserInfo: builder.mutation<IUserInfo, void>({
      query: () => ({
        url: `api/user/GetUserInfo`,
        method: 'GET'
      })
    }),
    getUsersAuth0: builder.mutation<
      IGetAllUsersResponse,
      {
        page: number;
        pageSize: number;
        filterByLabel: string;
        filterByRole: string;
      }
    >({
      query: (payload) => ({
        url: `api/user/GetAllUsersWithSystemRoleInfo`,
        method: 'GET',
        params: payload
      })
    }),
    postUserInfo: builder.mutation<{ userId: number }, IUserInfo>({
      query: (payload) => ({
        url: 'api/user',
        method: 'POST',
        body: payload
      })
    }),
    updateUserInfo: builder.mutation<any, IUserInfo>({
      query: (payload) => ({
        url: 'api/user',
        method: 'PUT',
        body: payload
      })
    }),
    getAllUsers: builder.mutation<any, {}>({
      query: () => ({
        url: 'api/User/AllUsers',
        method: 'GET'
      })
    }),
    getLocationsUsersByLocationId: builder.mutation<any, {}>({
      query: (locationId: number) => ({
        url: `api/Location/GetLocationUsersByLocationId/${locationId}`,
        method: 'GET'
      })
    }),
    deleteSystemUser: builder.mutation<any, number | undefined>({
      query: (userId: number) => ({
        url: `api/User?userId=${userId}`,
        method: 'DELETE'
      })
    }),
    deleteMultipleUsers: builder.mutation<any, number[]>({
      query: (usersIds) => ({
        url: `api/User/Multi?ids=${usersIds.join(',')}`,
        method: 'DELETE'
      })
    }),
    putChangePassword: builder.mutation<
      null,
      { currentPassword: string; newPassword: string }
    >({
      query: (payload) => ({
        url: 'api/User/ChangeCurrentUserPassword',
        method: 'PUT',
        body: payload
      }),
      transformErrorResponse: (response: any) => {
        return response.data.message;
      }
    }),
    changeUserPassword: builder.mutation<
      null,
      { email: string; password: string }
    >({
      query: (payload) => ({
        url: 'api/User/Password',
        method: 'PUT',
        body: payload
      })
    }),
    deleteMyAccount: builder.mutation<any, void>({
      query: () => ({
        url: 'api/User/MyAccount',
        method: 'DELETE'
      })
    }),
    getUserPendingActivation: builder.mutation<IUserPendingActivation[], void>({
      query: () => ({
        url: `api/User/AccountsUnverified`,
        method: 'GET'
      })
    }),
    deleteUsersPendingActivation: builder.mutation<any, number[]>({
      query: (ids) => ({
        url: `api/User/EmailToVerify?ids=${ids.join(',')}`,
        method: 'DELETE'
      })
    }),
    getUserOptCode: builder.mutation<[], void>({
      query: () => ({
        url: `api/User/AllSentOTPs`,
        method: 'GET'
      })
    }),
    updateUserStatus: builder.mutation<null, { email: string; status: number }>(
      {
        query: (payload) => ({
          url: 'api/User/UpdateStatus',
          method: 'POST',
          body: payload
        })
      }
    ),
    getUsersPasswordReset: builder.mutation<PasswordResetInfo[], void>({
      query: () => ({
        url: `api/User/PasswordResetRequest`,
        method: 'GET'
      })
    }),
    openUrlPassword: builder.mutation<null, { email: string; key: string }>({
      query: (payload) => ({
        url: 'api/User/ValidateResetPasswordStatus',
        method: 'POST',
        body: payload
      })
    })
  })
});

export const {
  useGetUserInfoMutation,
  usePostUserInfoMutation,
  useUpdateUserInfoMutation,
  useGetAllUsersMutation,
  useGetUsersAuth0Mutation,
  useGetLocationsUsersByLocationIdMutation,
  useDeleteSystemUserMutation,
  useDeleteMultipleUsersMutation,
  usePutChangePasswordMutation,
  useChangeUserPasswordMutation,
  useDeleteMyAccountMutation,
  useGetUserPendingActivationMutation,
  useDeleteUsersPendingActivationMutation,
  useGetUserOptCodeMutation,
  useUpdateUserStatusMutation,
  useGetUsersPasswordResetMutation,
  useOpenUrlPasswordMutation
} = apiUsers;
